import React from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { AlertTriangle, Home } from 'lucide-react';

/**
 * Route-level error element for React Router. Displays user-friendly message and details in dev.
 * Aligns with global ErrorBoundary visuals.
 */
export default function RouteError() {
  const error = useRouteError();

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again.';

  if (isRouteErrorResponse(error)) {
    title = `${error.status} ${error.statusText}`;
    try {
      message = typeof error.data === 'string' ? error.data : error.data?.message ?? message;
    } catch {
      // noop
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center border border-gray-200">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-error-50 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-error-600" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-600 mb-6">{message}</p>

        <button
          onClick={handleGoHome}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
        >
          <Home className="w-4 h-4 mr-2" />
          Go Home
        </button>

        {import.meta.env.DEV && !!error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">Error Details (Development)</summary>
            <pre className="mt-2 text-xs bg-gray-100 p-3 rounded overflow-auto text-gray-800 max-h-48">
              {isRouteErrorResponse(error) ? JSON.stringify(error, null, 2) : (error as any)?.stack || String(error)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
